// React Imports
import { Outlet } from "react-router-dom";
import { useContext, useEffect, useState, useRef } from "react";

// ANTD Imports
import { Alert, Button, Drawer, Spin, Layout, Row, Col, Modal, DatePicker, Select, Space } from "antd";
import type { SelectProps } from "antd"
import { HistoryOutlined, SearchOutlined } from '@ant-design/icons';
// Image/SVG Imports
import orgLogo from "../../assets/orgLogo.png"
import orgLogoLight from "../../assets/orglogo-full-white.png"
import NCSlogo from "../../assets/ncs_logo.png"
// import NCSlogoLight from "../../assets/ncs_logo-light.png"
import noReferenceIcon from "../../assets/no_reference.png"
import barIcon from "../../assets/svgs/bars-solid.svg"
import warningRedIcon from "../../assets/svgs/warning-red.svg"
import halfCircleIcon from "../../assets/svgs/circle-half-stroke-solid.svg"
import filterCancelIcon from "../../assets/svgs/filter-circle-xmark-solid.svg"
import frontLogo from "../../assets/frontLogo.png"


// Component Imports
import { SearchInput } from "../searchInput/SearchInput";
import { ChatHistoryPanel } from "../../components/ChatHistory/ChatHistoryPanel";
import UserContextMenu from "../../components/menu/UserContextMenu";
import SearchResultItem from "../../components/SearchResult/SearchResultItem";
import CitationListItem from "../../components/SearchResult/CitationListItem";
import { Loader } from "../../components/Loader/Loader";

// API Imports
import { generateFollowUpQuery, historyGenerate, mapChatMessageToSearchResult, classifyMessage, historyRead, getFAQs, historyGenerateStream, generateMoreQuestions } from "../../api";

// Context Imports
import { AppStateContext } from "../../state/AppProvider";
import { searchStatus } from "../../constants/enums";
import appNotificationInit, { appNotification } from "../../components/common/notification";
import { GENERAL_SERVER_ERROR, CITATION_HEADINGS_PREFIX, SEARCH_INPUT_PLACEHOLDER } from "../../constants/config";
import { CitationDisplayTypes } from "../../constants/enums"
import { getScreenSize, handleStreamResponse, redactPIData } from '../../utilities/utilities';

// CSS Imports
import './NewLayout.css';
import dayjs from 'dayjs';

const NewLayout = () => {
    const abortFuncs = useRef([] as AbortController[]);
    const [supportingDocumentUrl, setSupportingDocumentUrl] = useState<string>("");
    const [supportingDocumentFilename, setSupportingDocumentFilename] = useState<string>("");
    const appStateContext = useContext(AppStateContext)
    const { RangePicker } = DatePicker;

    // const [selectedSearchResult, setSelectedSearchResult] = useState<SearchResult>();
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [searchResults, setSearchResults] = useState<SearchResult[]>([])
    const [currentSearchStatus, setCurrentSearchStatus] = useState<searchStatus>(searchStatus.Done);
    const [searchingPlaceholderInfo, setSearchingPlaceholderInfo] = useState<SearchingPlaceholder>();
    const [currentSearchSessionId, setCurrentSearchSessionId] = useState<string>("");
    const [darkmodeActive, setDarkmodeActive] = useState<boolean>(false);
    const [endSessionModalOpen, setEndSessionModalOpen] = useState<boolean>(false);
    const [openReferencesModal, setOpenReferencesModal] = useState<boolean>(false);
    const [selectedReference, setSelectedReference] = useState<SearchResultCitation>();
    const [messageToClassify, setMessageToClassify] = useState<SearchResult | null>(null);
    const [activeSearch, setActiveSearch] = useState<boolean>(false);
    const [smallScreenMenuActive, setSmallScreenMenuActive] = useState<boolean>(false);
    const [followUpQueries, setFollowUpQueries] = useState<string[]>([]);
    // const [faqList, setFaqList] = useState<string[]>([]);
    const [queryTimeTaken, setQueryTimeTaken] = useState<number>(0);
    const [CH_filter_category, setCH_filter_category] = useState<SelectProps[]>([]);
    const [earliest_date, setEarliest_date] = useState<dayjs.Dayjs | null>(null);
    const [loading, setLoading] = useState(false);
    const [hasClicked, setHasClicked] = useState(false);
    const [isStreamActive, setIsStreamActive] = useState(false);

    appNotificationInit()

    // Function to handle supporting document upload
    const handleSupportingDocumentUploaded = (url: string, filename: string) => {
        setSupportingDocumentUrl(url);
        setSupportingDocumentFilename(filename);
    };

    const cleanMessageForDisplay = (message: string) => {
        if (!message) return "";
        
        const supportingDocPattern = /<supporting_document url="[^"]*" filename="([^"]*)"><\/supporting_document>/g;
        const matches = [...message.matchAll(supportingDocPattern)];
        
        let cleanMessage = message.replace(supportingDocPattern, '').trim();
        
        // If there were supporting documents, add file indicators
        if (matches && matches.length > 0) {
            const fileIndicators = matches.map(match => {
                const filename = match[1] || 'document';
                return `📎 ${filename}`;
            }).join(', ');
            
            cleanMessage = cleanMessage + (cleanMessage ? '\n' : '') + fileIndicators;
        }
        
        return cleanMessage;
    };

    useEffect(() => {
        if (smallScreenMenuActive) {
            document.getElementById('small-screen-overlay-menu')?.classList.add('open');
        } else {
            document.getElementById('small-screen-overlay-menu')?.classList.remove('open');
        }
    }, [smallScreenMenuActive])

    useEffect(() => {

        appStateContext?.state.isDarkMode ? document.body.classList.add("dark-mode") : document.body.classList.remove("dark-mode");
        setDarkmodeActive(document.body.classList.contains('dark-mode') ?? false)
        const observer = new MutationObserver(() => {
            setDarkmodeActive(document.body.classList.contains("dark-mode"));
        });

        observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        setLoading(false);

        function handleResize() {
            const size = getScreenSize();
            appStateContext?.dispatch({ type: 'SCREEN_SIZE', payload: size });
        }

        window.addEventListener("resize", handleResize);
        handleResize()
        return () => window.removeEventListener("resize", handleResize);


    }, [])

    useEffect(() => {
        let categoryList: string[] = [];
        let dateList: string[] = [];
        const addCategoriesToList = (categories: string[]) => {
            categories.forEach((category) => {
                if (!categoryList.includes(category)) {
                    categoryList.push(category);
                }
            });
        };

        appStateContext?.state.chatHistory?.forEach((chatHistoryItem) => {
            chatHistoryItem.messages.forEach((message) => {
                if (message.classification?.category3) {
                    addCategoriesToList(message.classification.category3);
                }
            });
            dateList.push(chatHistoryItem.createdAt)
        });

        // Sort the dateList and take the earliest date
        dateList.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
        const earliestDate = dateList[0] ? moment(dateList[0]) : null;
        //sort CategoryList alphabetically
        categoryList.sort((a, b) => a.localeCompare(b));
        setEarliest_date(earliestDate);
        setCH_filter_category(categoryList.map((category) => ({ label: category, value: category })));

    }, [appStateContext?.state.chatHistory])

    // useEffect(() => {
    //     onClickDrawer();
    // }, [appStateContext?.state.currentChat?.messages])

    // for rendering search results after searchresults are set.
    useEffect(() => {
        if (currentSearchStatus == searchStatus.Done) {
            setSearchingPlaceholderInfo({ userQuery: "", show: false });
            // setSelectedSearchResult(searchResults[0]);
            // appStateContext?.dispatch({ type: 'SET_SELECTED_SEARCHRESULT', payload: searchResults[0] }); 
        }
    }, [currentSearchStatus]);

    useEffect(() => {
        if (appStateContext?.state.screenSize && appStateContext?.state.screenSize < 3) {
            setSmallScreenMenuActive(false);
        }
    }, [appStateContext?.state.currentChat])

    useEffect(() => {
        if (messageToClassify) {
            classifyMessage({ message_id: messageToClassify.messageId!, answer: messageToClassify.generatedText!, createdAt: messageToClassify.generatedTextCreatedAt })
                .then((data) => {
                    if (data) {
                        setSearchResults((prev) => {
                            return prev.map((searchResult) => {
                                return (searchResult.id === messageToClassify.id) ? { ...searchResult, classification: data, } : searchResult;
                            });
                        });

                        const chatHistoryItem = appStateContext?.state.chatHistory?.find((h) => h.id === currentSearchSessionId)
                        if (chatHistoryItem) {
                            chatHistoryItem.messages = chatHistoryItem.messages.map((m) => {
                                if (m.message_id === messageToClassify.messageId) {
                                    return { ...m, classification: data }
                                }
                                else
                                    return m;
                            });
                            appStateContext?.dispatch({ type: "UPDATE_CHAT_HISTORY", payload: chatHistoryItem })
                        }
                        else {
                            //get conversation for session and update chat history
                            historyRead(currentSearchSessionId).then((currentConversation) => {
                                if (currentConversation)
                                    appStateContext?.dispatch({ type: "UPDATE_CHAT_HISTORY", payload: currentConversation })
                            })
                        }
                        setMessageToClassify(null);
                    }
                })
        }
    }, [messageToClassify, currentSearchSessionId])


    const handleSearchInputClick = (searchInputValue: string, conversationId: string) => {
        let userMessageToSend = redactPIData(searchInputValue);
        
        setCurrentSearchStatus(searchStatus.Processing);
        appStateContext?.dispatch({ type: 'SET_SELECTED_SEARCHRESULT', payload: undefined });
        // Show only the user's original query in the UI
        setSearchingPlaceholderInfo({ userQuery: userMessageToSend, show: true });
        
        if (import.meta.env.VITE_APP_STREAMED_RESULT === "DISABLED") {
            makeApiRequestWithDynamoDB(userMessageToSend, conversationId);
        } else {
            makeApiRequestWithDynamoDBStream(userMessageToSend, conversationId);
        }
        setFollowUpQueries([]);
        setLoading(false);
        setHasClicked(false);
    };

    const handleSearchInputClickSmallScreen = (value: boolean | undefined) => {
        if (value) {
            setActiveSearch(value);
            setLoading(false);

        } else {
            setActiveSearch(!activeSearch);
        }
    }

    // Callback function to handle searchresult clicked
    // const handleSelectSearchResult = (newSelectedSearchResult: SearchResult) => {
    //     setSelectedSearchResult(newSelectedSearchResult);
    // };

    const onClickDrawer = async () => {

        if (currentSearchStatus !== searchStatus.Processing) {
            try {
                let searchResults: SearchResult[] = []
                // fetch messages from chatHistoryPanel
                const messages = appStateContext?.state.currentChat?.messages;
                const agentId = appStateContext?.state.currentChat?.agent_id;


                const isActiveBedrockSession = appStateContext?.state.currentChat?.isRecent

                if (isActiveBedrockSession) {
                    setCurrentSearchStatus(searchStatus.Done)
                    setCurrentSearchSessionId(appStateContext.state.currentChat!.id)
                }
                else if (appStateContext?.state.currentChat !== null) {
                    setCurrentSearchStatus(searchStatus.Inactive)
                }

                if (messages) {
                    // let userMessage: ChatMessage
                    let assistantMessage: ChatMessage
                    // let toolMessage: ChatMessage
                    messages?.forEach((message: any) => {
                        if (message.role === "assistant") {
                            // If role is assistant, set generated text to content
                            assistantMessage = message
                            let searchResult = mapChatMessageToSearchResult(assistantMessage)
                            searchResults.unshift(searchResult)
                        }
                    });
                    setSearchResults(searchResults)
                    // setSelectedSearchResult(searchResults[0]);
                    appStateContext?.dispatch({ type: 'SET_SELECTED_SEARCHRESULT', payload: searchResults[0] });
                    setFollowUpQueries([])
                    if (agentId) {
                        const agent = appStateContext.state.agentOptions.find((agent) => agent.id === agentId)
                        if (agent) {
                            appStateContext?.dispatch({ type: 'SET_SELECTED_AGENT', payload: agent });
                        }
                    }

                    setLoading(false);
                }

            } catch (error) {
                console.error("Error fetching chat history or mapping responses:", error);
            }
        }
    };

    const getFollowUpQuery = async (conversationId: string) => {
        //generate followup query
        if (import.meta.env.VITE_APP_FOLLOWUP_QUESTIONS === "DISABLED")
            return
        await generateFollowUpQuery(conversationId).then((res) => {
            if (res) {
                // console.log("followup response:", res)
                setFollowUpQueries(res.questions)
            }
        })
    }

    const toggleDrawer = () => {
        setIsDrawerOpen(!isDrawerOpen);
    };
    const makeApiRequestWithDynamoDBStream = async (searchQuery: string, conversationId?: string) => {
        const abortController = new AbortController();
        abortFuncs.current.unshift(abortController);
        const startTime = Date.now();
        setQueryTimeTaken(0);
        
        // Add supporting document URL right before sending to API (hidden from UI)
        let finalSearchQuery = searchQuery;
        if (supportingDocumentUrl) {
            finalSearchQuery += `\n<supporting_document url="${supportingDocumentUrl}" filename="${supportingDocumentFilename}"></supporting_document>`;
            setSupportingDocumentUrl("");
            setSupportingDocumentFilename("");
        }
        
        let userMessage: ChatUserMessage
        userMessage = {
            conversation_id: conversationId,
            user_query: finalSearchQuery,
            createdAt: new Date().toISOString(),
            agent_id: appStateContext?.state.selectedAgent?.id!
        };
        const streamResult = await historyGenerateStream(userMessage, abortController.signal);
        const chunks = handleStreamResponse(streamResult)
        let initial: boolean = true;
        let currentSearchResult: SearchResult | undefined = undefined
        let chatResponse: ChatResponse | undefined = undefined
        let jsonStrBeginsWith = '{"user_id":'
        for await (let valueStr of chunks) {
            setIsStreamActive(true);
            // console.log("value", valueStr);
            chatResponse = undefined
            let startIndex = valueStr.lastIndexOf(jsonStrBeginsWith);
            let endIndex = valueStr.length
            let jsonStr = valueStr.substring(startIndex);

            while (chatResponse === undefined && startIndex !== -1) {
                // console.log("json value", jsonStr);
                try {
                    chatResponse = JSON.parse(jsonStr);
                    // console.log("chatResponse", chatResponse)
                }
                catch (e) {
                    console.log("json parse error", e);
                    endIndex = startIndex;
                    startIndex = valueStr.substring(0, startIndex - 1).lastIndexOf(jsonStrBeginsWith);
                    jsonStr = valueStr.substring(startIndex, endIndex);
                }
            }
            // console.log("chatResponse", chatResponse)
            if (chatResponse) {
                if (initial) {
                    setQueryTimeTaken((Date.now() - startTime) / 1000);
                    setCurrentSearchSessionId(chatResponse.conversation_id);
                    setCurrentSearchStatus(searchStatus.Done);
                    setSearchingPlaceholderInfo({ userQuery: "", show: false });
                }
                currentSearchResult = mapChatMessageToSearchResult(chatResponse)
                // console.log("currentSearchResult:", currentSearchResult)
                setSearchResults([currentSearchResult, ...searchResults.filter((result) => result.id !== currentSearchResult!.id)]);
                // setSelectedSearchResult(currentSearchResult);
                appStateContext?.dispatch({ type: 'SET_SELECTED_SEARCHRESULT', payload: currentSearchResult });
            }
            initial = false
        }

        setIsStreamActive(false);

        //tasks after end of stream
        if (currentSearchResult && chatResponse) {
            const chatHistoryItem = appStateContext?.state.chatHistory?.find((h) => h.id === chatResponse.conversation_id)
            if (chatHistoryItem) {
                // chatHistoryItem.messages.push(
                //     {
                //         content: userMessage.content,
                //         role: "user",
                //         createdAt: userMessage.createdAt,
                //     })
                chatHistoryItem.messages.push(
                    {
                        // content: chatResponse.content,
                        user_query: chatResponse.user_query,
                        assistant_reply: chatResponse.assistant_reply,
                        role: "assistant",
                        createdAt: chatResponse.createdAt,
                        citations: chatResponse.citations,
                        message_id: chatResponse.message_id
                    })
                appStateContext?.dispatch({ type: "UPDATE_CHAT_HISTORY", payload: chatHistoryItem })
            }
            else {
                historyRead(chatResponse.conversation_id).then((currentConversation) => {
                    if (currentConversation) {
                        appStateContext?.dispatch({ type: "UPDATE_CHAT_HISTORY", payload: currentConversation })
                        appStateContext?.dispatch({ type: "UPDATE_CURRENT_CHAT", payload: currentConversation })
                    }
                })
            }
            setMessageToClassify(currentSearchResult);
            getFollowUpQuery(chatResponse.conversation_id)
        }
    }
    const makeApiRequestWithDynamoDB = async (searchQuery: string, conversationId?: string) => {
        const abortController = new AbortController();
        abortFuncs.current.unshift(abortController);
        const startTime = Date.now();
        setQueryTimeTaken(0);
        
        // Add supporting document URL right before sending to API (hidden from UI)
        let finalSearchQuery = searchQuery;
        if (supportingDocumentUrl) {
            finalSearchQuery += `\n<supporting_document url="${supportingDocumentUrl}" filename="${supportingDocumentFilename}"></supporting_document>`;
            setSupportingDocumentUrl("");
            setSupportingDocumentFilename("");
        }
        
        let userMessage: ChatUserMessage
        userMessage = {
            conversation_id: conversationId,
            user_query: finalSearchQuery,
            createdAt: new Date().toISOString(),
            agent_id: appStateContext?.state.selectedAgent?.id!
        };

        try {
            await historyGenerate(userMessage, abortController.signal)
                .then((chatResponse) => {
                    if (chatResponse) {
                        setQueryTimeTaken((Date.now() - startTime) / 1000);
                        // const chatResponse = data as ChatResponse;
                        setCurrentSearchSessionId(chatResponse.conversation_id!);
                        const chatHistoryItem = appStateContext?.state.chatHistory?.find((h) => h.id === chatResponse.conversation_id)
                        if (chatHistoryItem) {
                            // chatHistoryItem.messages.push(
                            //     {
                            //         content: userMessage.content,
                            //         role: "user",
                            //         createdAt: userMessage.createdAt,
                            //     })
                            chatHistoryItem.messages.push(
                                {
                                    user_query: chatResponse.user_query,
                                    assistant_reply: chatResponse.assistant_reply,
                                    role: "assistant",
                                    createdAt: chatResponse.createdAt,
                                    citations: chatResponse.citations,
                                    message_id: chatResponse.message_id
                                })
                            appStateContext?.dispatch({ type: "UPDATE_CHAT_HISTORY", payload: chatHistoryItem })
                        }
                        else {
                            historyRead(chatResponse.conversation_id!).then((currentConversation) => {
                                if (currentConversation) {
                                    appStateContext?.dispatch({ type: "UPDATE_CHAT_HISTORY", payload: currentConversation })
                                    appStateContext?.dispatch({ type: "UPDATE_CURRENT_CHAT", payload: currentConversation })
                                }
                            })
                        }
                        const currentSearchResult = mapChatMessageToSearchResult(chatResponse)
                        setSearchResults([currentSearchResult, ...searchResults]);
                        setMessageToClassify(currentSearchResult);
                        getFollowUpQuery(chatResponse.conversation_id!)
                        // setSelectedSearchResult(currentSearchResult);
                        appStateContext?.dispatch({ type: 'SET_SELECTED_SEARCHRESULT', payload: currentSearchResult });
                    }
                });

        } catch (e) {
            console.log("an error invoking history generate has occurred.")
            if (!abortController.signal.aborted) {
                appNotification.error({ message: "Search", description: GENERAL_SERVER_ERROR });
            }
        }
        setCurrentSearchStatus(searchStatus.Done);
        setSearchingPlaceholderInfo({ userQuery: "", show: false });
        return abortController.abort();
    }

    useEffect(() => {
        if (appStateContext?.state.selectedSearchResult) {
            setSearchingPlaceholderInfo({ userQuery: '', show: true });
        }

    }, [appStateContext?.state.selectedSearchResult]);

    // useEffect(() => {
    //     // When search results are updated, update the data of the selected search result
    //     if (searchResults.length > 0 && selectedSearchResult) {
    //         const newSelectedSearchResult = searchResults.find((searchResult) => searchResult.id === selectedSearchResult.id);
    //         if (newSelectedSearchResult) {
    //             setSelectedSearchResult(newSelectedSearchResult);
    //         }
    //     }

    // }, [searchResults])

    const newSession = () => {
        setCurrentSearchStatus(searchStatus.New);
        setSearchResults([]);
        // setSelectedSearchResult(undefined);
        appStateContext?.dispatch({ type: 'SET_SELECTED_SEARCHRESULT', payload: undefined });
        setCurrentSearchSessionId("");
        appStateContext!.state.currentChat = null;
        appStateContext?.dispatch({ type: 'UPDATE_SEARCH_QUERY', payload: "" })
        appStateContext?.dispatch({ type: "SET_REFRESH_SEARCH_HISTORY", payload: appStateContext.state.refreshSearchHistoryCounter + 1 });
    };

    const handleNewChat = () => {
        setEndSessionModalOpen(true);
    }

    const updateFeedbackToSearchResult = (searchResultId: string, feedbackRating: boolean | null, feedbackComment: string) => {
        setSearchResults((prev) => {
            return prev.map((searchResult) => {
                if (searchResult.id === searchResultId) {
                    return {
                        ...searchResult,
                        feedbackRating: feedbackRating,
                        feedbackComment: feedbackComment
                    };
                }
                return searchResult;
            });
        });
    }

    const updateClipboardActivityToSearchResult = (searchResultId: string, clipboardActivityCount: number) => {
        setSearchResults((prev) => {
            return prev.map((searchResult) => {
                if (searchResult.id === searchResultId) {
                    return {
                        ...searchResult,
                        clipboardActivityCount: clipboardActivityCount
                    };
                }
                return searchResult;
            });
        });
    }

    const toggleTheme = () => {
        document.body.classList.toggle("dark-mode");
        localStorage.setItem('darkmode', document.body.classList.contains("dark-mode") ? '1' : '0');
        appStateContext?.dispatch({ type: "TOGGLE_DARK_MODE" });
    }

    const handleCatChangeFilter = (value: string[]) => {
        if (appStateContext?.state.chFilter) {
            appStateContext.state.chFilter.category_options = value;
            appStateContext?.dispatch({ type: "UPDATE_CH_FILTER", payload: appStateContext.state.chFilter });
        }
    };

    function moment(date: string): dayjs.Dayjs | null {
        return date ? dayjs(date) : null;
    }

    const displaySearchResults = () => {
        const selectedIndex = searchResults.findIndex((sr) => sr.id === appStateContext?.state.selectedSearchResult?.id);
        return searchResults.map((searchResult, index) => {
            return (
                <>
                    <SearchResultItem
                        key={searchResult.id}
                        isSelected={searchResult.id === appStateContext?.state.selectedSearchResult?.id}
                        searchResult={searchResult}
                        selectedSearchResultIndex={selectedIndex}
                        searchResultIndex={index}
                        updateFeedbackToSearchResult={updateFeedbackToSearchResult}
                        updateClipboardActivityToSearchResult={updateClipboardActivityToSearchResult}
                        handleSelectSearchResult={(selectedSearchResult) => {
                            // setSelectedSearchResult(selectedSearchResult);
                            appStateContext?.dispatch({ type: 'SET_SELECTED_SEARCHRESULT', payload: selectedSearchResult });
                            // Auto scroll to the element with the id of user-query
                            const element = document.getElementById("user-query");
                            if (element && currentSearchStatus !== searchStatus.Processing) {
                                element.scrollIntoView({ behavior: "smooth", block: "center" });
                            }
                        }}
                        handleSelectedReference={(reference) => { setSelectedReference(reference); setOpenReferencesModal(true); }}
                        screenSize={appStateContext?.state.screenSize ?? 3}
                        currentSearchStatus={currentSearchStatus}
                    />
                    {index === 0 && import.meta.env.VITE_APP_SHOW_QUERY_TIMETAKEN === "ENABLED" && `${queryTimeTaken.toFixed(1)} s`}
                </>)
        })
    }

    return (
        <Layout className='d-flex d-flex-column bg-none' style={{ height: '100vh' }}>
            {(appStateContext?.state.screenSize ?? 3) > 2 ?
                (
                    <Layout.Header className='d-flex g-3 bg-none' style={{ height: '10vh' }}>
                        {import.meta.env.VITE_APP_SHOW_ORG_LOGO === "ENABLED" && <img alt="Organisation logo"
                            src={orgLogo}
                            className='my-auto filter-normal-white'
                            aria-hidden="true"
                            style={{ height: '50px', width: 'auto' }}
                        />}
                        <img alt="NCS logo"
                            src={NCSlogo}
                            className='my-auto filter-normal-white'
                            aria-hidden="true"
                            style={{ height: '20px', width: 'auto' }}
                        />
                        {(searchResults.length > 0 || searchingPlaceholderInfo?.userQuery) &&
                            <div className='w-100 me-4 d-flex'>
                                <SearchInput
                                    currentSearchStatus={currentSearchStatus}
                                    placeholder={SEARCH_INPUT_PLACEHOLDER}
                                    conversationId={currentSearchSessionId}
                                    handleSearchBtnClick={handleSearchInputClick}
                                    handleSearchInputClickSmallScreen={handleSearchInputClickSmallScreen}
                                    activeSearch={activeSearch}
                                    className='flex-grow-1'
                                    size={appStateContext?.state.screenSize ?? 3}
                                    onSupportingDocumentUploaded={handleSupportingDocumentUploaded}
                                />
                                {supportingDocumentUrl && (
                                    <div className='ms-2 px-2 py-1 bg-primary br-8 d-flex my-auto' style={{height: '32px', alignItems: 'center'}}>
                                        <span className='supporting-document-title-text'>{supportingDocumentFilename}</span>
                                        <div>
                                            <button 
                                                className='ms-2 bg-transparent b-none c-root d-flex'
                                                onClick={() => setSupportingDocumentUrl("")}
                                                style={{ cursor: 'pointer' }}
                                            >   
                                                ✕
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        }
                        {searchResults.length > 0 && <Button
                            className='btn-primary my-auto fs16-fw600 bg-root b-primary c-primary b-none br-16 pt-2 pb-2'
                            disabled={searchResults.length === 0}
                            onClick={handleNewChat}
                        >
                            New
                        </Button>}
                        <Button
                            className={`${(searchResults.length <= 0) ? 'ms-auto' : ''} btn-primary my-auto fs16-fw600 bg-root b-primary c-primary b-none br-16 pt-2 pb-2`}
                            icon={<HistoryOutlined />}
                            onClick={toggleDrawer}
                        >
                            History
                        </Button>
                        <UserContextMenu />
                    </Layout.Header>
                ) : (
                    <Layout.Header className='bg-root px-3' style={{ height: '15vh', position: 'fixed', top: 0, width: '100%', zIndex: 1 }}>
                        <div className='d-flex g-3 mt-2 mb-1'>
                            <button className='d-flex h-fit-content my-auto' onClick={() => { setSmallScreenMenuActive(!smallScreenMenuActive) }}>
                                <img
                                    className='filter-primary'
                                    src={barIcon}
                                    alt=""
                                    style={{ height: '30px', width: 'auto' }}
                                />
                            </button>
                            {import.meta.env.VITE_APP_SHOW_ORG_LOGO === "ENABLED" && <img alt="Organisation logo"
                                src={orgLogo}
                                className='my-auto filter-normal-white'
                                aria-hidden="true"
                                style={{ height: '40px', width: 'auto' }}
                            />}
                            <img alt="NCS logo"
                                src={NCSlogo}
                                className='my-auto filter-normal-white'
                                aria-hidden="true"
                                style={{ height: '18px', width: 'auto' }}
                            />
                        </div>
                        <div className='bg-root'>
                            <SearchInput
                                currentSearchStatus={currentSearchStatus}
                                placeholder={SEARCH_INPUT_PLACEHOLDER}
                                conversationId={currentSearchSessionId}
                                handleSearchBtnClick={handleSearchInputClick}
                                handleSearchInputClickSmallScreen={handleSearchInputClickSmallScreen}
                                activeSearch={true}
                                size={appStateContext?.state.screenSize ?? 3}
                                onSupportingDocumentUploaded={handleSupportingDocumentUploaded}
                            />
                            {supportingDocumentUrl && (
                                <div className='px-2 py-1 bg-primary br-8 d-flex mt-2 justify-content-between' style={{height: '32px', alignItems: 'center'}}>
                                    <span className='supporting-document-title-text' style={{width: 'fit-content'}}>{supportingDocumentFilename}</span>
                                    <div>
                                        <button 
                                            className='ms-2 bg-transparent b-none c-root d-flex'
                                            onClick={() => setSupportingDocumentUrl("")}
                                            style={{ cursor: 'pointer' }}
                                        >   
                                            ✕
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </Layout.Header>
                )
            }
            {appStateContext?.state.screenSize && appStateContext?.state.screenSize > 2 ?
                (
                    <>
                        <Modal
                            title={
                                <div style={{ display: 'flex' }}>
                                    <img src={warningRedIcon} alt="" style={{ marginTop: 'auto', marginBottom: 'auto', marginRight: '5px' }} />
                                    <div className="ant-modal-title">You are about to end the session!</div>
                                </div>
                            }
                            style={{
                                borderRadius: '16px',
                                padding: '0px',
                                boxShadow: '0px 10px 20px -8px #392EA433'
                            }}
                            className={`custom-modal c-default ${(appStateContext?.state?.screenSize ?? 3) < 3 ? 'small-screen-modal' : ''}`}
                            okButtonProps={{
                                className: 'bg-primary br-16 c-root pt-2 pb-2'
                            }}
                            cancelButtonProps={{
                                className: 'bg-root br-16 c-primary b-primary pt-2 pb-2'
                            }}
                            open={endSessionModalOpen}
                            onOk={() => {
                                newSession();
                                setEndSessionModalOpen(false);
                                setFollowUpQueries([]);
                            }}
                            onCancel={() => setEndSessionModalOpen(false)}
                            okText="Yes, end session"
                            cancelText="No, go back"
                        >
                            <p className='c-default' style={{ paddingBottom: '20px', fontSize: '16px' }}>You are about to end the session. Do you want to continue?</p>
                        </Modal>
                        <Modal
                            title={
                                selectedReference?.main_title
                            }
                            open={openReferencesModal}
                            onCancel={() => {
                                setOpenReferencesModal(false);
                                setSelectedReference(undefined);
                            }}
                            cancelText="Close"
                            cancelButtonProps={{
                                className: 'bg-primary br-16 c-root b-primary pt-2 pb-2'
                            }}
                            okButtonProps={{
                                className: 'bg-primary br-16 c-root b-primary pt-2 pb-2',
                                style: { display: 'none' }
                            }}
                            className={`custom-modal ${(appStateContext?.state?.screenSize ?? 3) < 3 ? 'small-screen-modal' : ''}`}
                        >
                            {selectedReference && (
                                <>
                                    <div className='fs16-fw600 c-default'>
                                        {selectedReference.display_type === CitationDisplayTypes.FULL || selectedReference.display_type === CitationDisplayTypes.MAIN_TITLE_WITH_SUBTITLE || selectedReference.display_type === CitationDisplayTypes.CUSTOM_URL
                                            ? (
                                                <div className='fs16-fw600 c-default'>
                                                    {CITATION_HEADINGS_PREFIX}
                                                    {decodeURIComponent(selectedReference.title ?? '')}
                                                </div>
                                            )
                                            : ``
                                        }
                                        {selectedReference.display_type === CitationDisplayTypes.FULL || selectedReference.display_type === CitationDisplayTypes.MAIN_TITLE_WITH_URL
                                            ? (
                                                <a
                                                    href={decodeURIComponent(
                                                        selectedReference.encoded_url ?? ''
                                                    )}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className='fs14-fw500 c-default t-underline'>
                                                    {decodeURIComponent(
                                                        selectedReference.encoded_url ?? ''
                                                    )}
                                                </a>
                                            )
                                            : ``
                                        }
                                        {(selectedReference.display_type === CitationDisplayTypes.CUSTOM_URL && selectedReference.url) && (
                                            <div className='fs14-fw400 c-default cur-pointer t-underline' onClick={() => window.open(selectedReference.url, '_blank')}>
                                                {selectedReference.url}
                                            </div>
                                        )}
                                    </div>

                                    <div className='bg-white p-3 br-16'>

                                        <div className="fs16-fw400 c-default references-modal-text">
                                            {selectedReference.content.split("\n").map((line, index) => (
                                                <span key={index}>{line}<br /></span>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            )}
                        </Modal>

                        <Layout.Content className='d-flex-grow d-flex-column d-flex justify-content-start' style={{ paddingLeft: '50px', paddingRight: '50px', paddingBottom: '20px', justifyContent: 'center', height: '90vh' }}>
                            <div className="pb-4 fs14-fw400 c-primary d-flex" style={{ whiteSpace: 'nowrap', flexWrap: 'wrap', gap: '8px 14px' }}>
                                {followUpQueries?.map((followUpQuery, index) =>
                                    <div key={index}
                                        className="tag b-lightblue"
                                        style={{ cursor: 'pointer', margin: '1px', wordWrap: 'break-word' }}
                                        onClick={() => {
                                            //appStateContext?.dispatch({ type: 'UPDATE_SEARCH_QUERY', payload: followUpQuery })
                                            handleSearchInputClick(followUpQuery, currentSearchSessionId);
                                        }} >{followUpQuery}</div>
                                )
                                }
                                {followUpQueries?.length > 1 && !hasClicked && (
                                    <div className='d-flex mt-1'>
                                        {loading ? (
                                            <div className="spinner-login mx-auto" role="status">
                                            </div>
                                        ) : (
                                            <button
                                                className='more-questions-btn mx-auto bg-blue ps-3 pe-3 p-1 br-16 c-default fs14-fw400 b-primary w-fit-content'
                                                onClick={async () => {
                                                    try {
                                                        setLoading(true);
                                                        const response = await generateMoreQuestions(followUpQueries);
                                                        if (response?.questions?.length) {
                                                            setFollowUpQueries((prev) => [...prev, ...response.questions]);
                                                        }
                                                    } catch (err) {
                                                        console.error('Failed to fetch more questions', err);
                                                    } finally {
                                                        setLoading(false);
                                                        setHasClicked(true);
                                                    }
                                                }}
                                            >
                                                More Questions
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className='bg-white-a-50 big-white-box pt-4 pe-4 ps-4 pb-4 br-20 d-flex-shrink'
                                style={{ display: `${searchResults.length > 0 || searchingPlaceholderInfo?.userQuery ? 'block' : 'none'}`, minHeight: '0' }}>
                                <Row id='big-white-box' className='h-100 d-flex'>
                                    <Col span={14} className='h-100 custom-scrollbar' style={{ overflowY: 'scroll' }}>
                                        {!(appStateContext?.state.selectedSearchResult?.userQuery) ? (
                                            <div className='bg-root p-3 br-tl-16 br-bl-16 b-2F4250'>
                                                <div id='user-query' className='fs20-fw600 c-primary ps-1'>
                                                    {cleanMessageForDisplay(searchingPlaceholderInfo?.userQuery || "")}
                                                    <div className='spinner mt-2'></div>
                                                </div>
                                            </div>
                                        ) : null}
                                        {displaySearchResults()}

                                    </Col>
                                    <Col span={10} className='custom-scrollbar bg-root br-tr-16 br-br-16 p-3 h-100' style={{ overflowY: 'scroll' }}>
                                        {appStateContext?.state.selectedSearchResult?.citations && appStateContext?.state.selectedSearchResult?.citations.length > 0 ?
                                            (
                                                <div id='references' className='c-offmain fs20-fw600'>References</div>
                                            ) : searchingPlaceholderInfo?.userQuery || isStreamActive ? (
                                                <div className='h-100 w-100 d-flex d-flex-column g-3'>
                                                    <Loader />
                                                    <Loader />
                                                    <Loader />
                                                </div>
                                            ) : (
                                                <div className='h-100 d-flex' style={{ alignItems: 'center', justifyContent: 'center' }}>
                                                    <img className='o-10 my-auto mx-auto filter-white-light' style={{ height: '50px', width: '50px' }} src={noReferenceIcon} alt="" />
                                                </div>
                                            )
                                        }
                                        {appStateContext?.state.selectedSearchResult?.citations && appStateContext?.state.selectedSearchResult?.citations.length > 0 &&
                                            (
                                                <div id='references-area' className='d-flex d-flex-column g-4 p-2 ps-1' key={`references-${appStateContext?.state.selectedSearchResult?.messageId}`}>
                                                    {import.meta.env.VITE_APP_GROUP_CITATIONS === "ENABLED" ? Object.values(
                                                        appStateContext?.state.selectedSearchResult?.citations.reduce((acc, citation, index) => {
                                                            if (!citation.chunk_id) {
                                                                acc[index] = { citation, citationNos: [citation.no] };
                                                            } else if (!acc[citation.chunk_id]) {
                                                                acc[citation.chunk_id] = { citation, citationNos: [citation.no] };
                                                            } else {
                                                                acc[citation.chunk_id].citationNos.push(citation.no);
                                                            }
                                                            return acc;
                                                        }, {} as Record<string, { citation: SearchResultCitation, citationNos: number[] }>)
                                                    ).map(({ citation, citationNos }) => (
                                                        <CitationListItem
                                                            key={`${appStateContext?.state.selectedSearchResult?.messageId}-${citation.chunk_id}`}
                                                            citation={citation}
                                                            citationNos={citationNos}
                                                            setSelectedReferences={setSelectedReference}
                                                            setOpenReferencesModal={setOpenReferencesModal}
                                                        />
                                                    )) :
                                                        appStateContext?.state.selectedSearchResult?.citations.map((citation, index) => (
                                                            <CitationListItem
                                                                key={`${appStateContext?.state.selectedSearchResult?.messageId}-${citation.no}-${index}`}
                                                                citation={citation}
                                                                citationNos={[citation.no]} // wrap in array to stay compatible with the grouped version
                                                                setSelectedReferences={setSelectedReference}
                                                                setOpenReferencesModal={setOpenReferencesModal}
                                                            />
                                                        ))
                                                    }
                                                </div>
                                            )
                                        }
                                    </Col>

                                </Row>
                            </div>
                            <div className='my-auto mx-auto w-100' style={{ display: `${searchResults.length > 0 || searchingPlaceholderInfo?.userQuery ? 'none' : 'block'}` }}>
                                {import.meta.env.VITE_APP_SHOW_ORG_LOGO === "ENABLED" && frontLogo && (
                                    <img src={frontLogo} className='filter-normal-white mx-auto d-flex ' style={{ maxHeight: '200px' }} alt="" />
                                )}       
                                <div className='mx-auto c-primary t-a-center mb-2 fs16-fw600'>{appStateContext.state.selectedAgent?.name}</div>
                                <div className='d-flex justify-content-center align-items-center'>
                                    <SearchInput
                                        currentSearchStatus={currentSearchStatus}
                                        placeholder={SEARCH_INPUT_PLACEHOLDER}
                                        conversationId={currentSearchSessionId}
                                        handleSearchBtnClick={handleSearchInputClick}
                                        handleSearchInputClickSmallScreen={handleSearchInputClickSmallScreen}
                                        activeSearch={activeSearch}
                                        className='w-80'
                                        size={appStateContext?.state.screenSize ?? 3}
                                        onSupportingDocumentUploaded={handleSupportingDocumentUploaded}
                                    />
                                    {supportingDocumentUrl && (
                                        <div className='ms-2 px-2 py-1 bg-primary br-8 d-flex align-items-center'>
                                            <span className='fs12-fw500 c-root me-1'>📎</span>
                                            <span className='fs12-fw500 c-root'>{supportingDocumentFilename}</span>
                                            <button 
                                                className='ms-1 bg-transparent b-none c-root fs10'
                                                onClick={() => setSupportingDocumentUrl("")}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {
                                    import.meta.env.VITE_APP_SHOW_FAQ === "ENABLED" && appStateContext?.state.faqList && appStateContext?.state.faqList.length > 0 && (
                                        <div className='d-flex d-flex-column g-3 mt-3'>
                                            {appStateContext?.state.faqList.map((faq, index) => {
                                                return (
                                                    <button
                                                        key={index}
                                                        className='mx-auto bg-root ps-3 pe-3 p-1 c-default fs14-fw400 b-lightblue w-fit-content'
                                                        style={{ borderRadius: 4 }}
                                                        onClick={() => {
                                                            handleSearchInputClick(faq, currentSearchSessionId);
                                                        }}
                                                    >
                                                        {faq}
                                                    </button>
                                                )
                                            })}
                                        </div>
                                    )
                                }
                                {
                                    !hasClicked && (
                                        <div className='d-flex d-flex-column g-3 mt-3'>
                                            {loading ? (
                                                <div className="spinner-login mx-auto" role="status">
                                                </div>
                                            ) : (
                                                <button
                                                    className='more-questions-btn mx-auto bg-root ps-3 pe-3 p-1 br-16 c-default fs14-fw400 b-primary w-fit-content'
                                                    onClick={async () => {
                                                        try {
                                                            setLoading(true);
                                                            const response = await generateMoreQuestions(appStateContext?.state.faqList);
                                                            if (response?.questions?.length) {
                                                                appStateContext?.dispatch({ type: 'SET_FAQ_LIST', payload: [...appStateContext.state.faqList, ...response.questions] });
                                                            }
                                                        } catch (err) {
                                                            console.error("Failed to fetch more questions", err);
                                                        } finally {
                                                            setLoading(false);
                                                            setHasClicked(true);
                                                        }
                                                    }}
                                                >
                                                    More Questions
                                                </button>
                                            )}
                                        </div>
                                    )
                                }
                            </div>

                        </Layout.Content>
                        <Layout.Footer style={{ height: '3vh' }} className='bg-none p-0 d-flex'>
                            <div className='o-50 c-default my-auto ms-5 fs10-fw400'>All responses are AI generated. Use at your own discretion.</div>
                            <div className='o-50 c-default my-auto ms-auto me-5 fs10-fw400'>v1.1.0</div>
                        </Layout.Footer>
                    </>

                ) : (
                    <>
                        <Modal
                            title={
                                <div style={{ display: 'flex' }}>
                                    <img src={warningRedIcon} alt="" style={{ marginTop: 'auto', marginBottom: 'auto', marginRight: '5px' }} />
                                    <div className="ant-modal-title">You are about to end the session!</div>
                                </div>
                            }
                            style={{
                                borderRadius: '16px',
                                padding: '0px',
                                boxShadow: '0px 10px 20px -8px #392EA433'
                            }}
                            className={`custom-modal-mobile c-default ${(appStateContext?.state?.screenSize ?? 3) < 3 ? 'small-screen-modal' : ''}`}
                            okButtonProps={{
                                className: 'bg-primary br-16 c-root pt-2 pb-2'
                            }}
                            cancelButtonProps={{
                                className: 'bg-root br-16 c-primary b-primary pt-2 pb-2'
                            }}
                            open={endSessionModalOpen}
                            onOk={() => {
                                newSession();
                                setEndSessionModalOpen(false);
                                setFollowUpQueries([]);
                            }}
                            onCancel={() => setEndSessionModalOpen(false)}
                            okText="Yes, end session"
                            cancelText="No, go back">
                            <p className='c-default' style={{ paddingBottom: '20px', fontSize: '14px' }}>You are about to end the session. Do you want to continue?</p>
                        </Modal>
                        <Modal
                            title={
                                selectedReference?.main_title
                            }
                            open={openReferencesModal}
                            onCancel={() => {
                                setOpenReferencesModal(false);
                                setSelectedReference(undefined);
                            }}
                            cancelText="Close"
                            cancelButtonProps={{
                                className: 'bg-primary br-16 c-root b-primary pt-2 pb-2'
                            }}
                            okButtonProps={{
                                className: 'bg-primary br-16 c-root b-primary pt-2 pb-2',
                                style: { display: 'none' }
                            }}
                            className={`custom-modal ${(appStateContext?.state?.screenSize ?? 3) < 3 ? 'small-screen-modal' : ''}`}
                        >
                            {selectedReference && (
                                <>
                                    <div className='fs16-fw600 c-default'>
                                        {selectedReference.display_type === CitationDisplayTypes.FULL || selectedReference.display_type === CitationDisplayTypes.MAIN_TITLE_WITH_SUBTITLE || selectedReference.display_type === CitationDisplayTypes.CUSTOM_URL
                                            ? (
                                                <div className='fs16-fw600 c-default'>
                                                    {CITATION_HEADINGS_PREFIX}
                                                    {decodeURIComponent(selectedReference.title ?? '')}
                                                </div>
                                            )
                                            : ``
                                        }
                                        {selectedReference.display_type === CitationDisplayTypes.FULL || selectedReference.display_type === CitationDisplayTypes.MAIN_TITLE_WITH_URL
                                            ? (
                                                <a
                                                    href={decodeURIComponent(
                                                        selectedReference.encoded_url ?? ''
                                                    )}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className='fs14-fw500 c-default t-underline'>
                                                    {decodeURIComponent(
                                                        selectedReference.encoded_url ?? ''
                                                    )}
                                                </a>
                                            )
                                            : ``
                                        }
                                        {(selectedReference.display_type === CitationDisplayTypes.CUSTOM_URL && selectedReference.url) && (
                                            <div className='fs14-fw400 c-default cur-pointer t-underline' onClick={() => window.open(selectedReference.url, '_blank')}>
                                                {selectedReference.url}
                                            </div>
                                        )}
                                    </div>

                                    <div className='bg-white p-3 br-16'>
                                        <div className="fs16-fw400 c-default references-modal-text">
                                            {selectedReference.content.split("\n").map((line, index) => (
                                                <span key={index}>{line}<br /></span>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            )}
                        </Modal>
                        <Layout.Content className='d-flex-grow w-100' style={{ paddingLeft: '0px', paddingRight: '0px', paddingBottom: '0px', display: `${searchResults.length > 0 || searchingPlaceholderInfo?.userQuery ? 'block' : 'flex'}`, flexDirection: `${searchResults.length > 0 || searchingPlaceholderInfo?.userQuery ? 'row' : 'column'}`, justifyContent: 'center', height: '85vh', position: 'absolute', top: '15vh' }}>
                            <div className="pb-2 fs14-fw400 c-primary">
                                {followUpQueries?.map((followUpQuery, index) =>
                                    <div className="tag b-lightblue" key={index}
                                        style={{ cursor: 'pointer', margin: '1px', wordBreak: 'break-word' }}
                                        onClick={() => {
                                            // appStateContext?.dispatch({ type: 'UPDATE_SEARCH_QUERY', payload: followUpQuery })
                                            handleSearchInputClick(followUpQuery, currentSearchSessionId);
                                        }} >{followUpQuery}</div>
                                )
                                }
                                {followUpQueries?.length > 1 && !hasClicked && (
                                    <div className='d-flex mt-1'>
                                        {loading ? (
                                            <div className="spinner-login mx-auto" role="status">
                                            </div>
                                        ) : (
                                            <button
                                                className='more-questions-btn mx-auto bg-blue ps-3 pe-3 p-1 br-16 c-default fs14-fw400 b-primary w-fit-content'
                                                onClick={async () => {
                                                    try {
                                                        setLoading(true);
                                                        const response = await generateMoreQuestions(followUpQueries);
                                                        if (response?.questions?.length) {
                                                            setFollowUpQueries(prev => [...prev, ...response.questions]);
                                                        }
                                                    } catch (err) {
                                                        console.error("Failed to fetch more questions", err);
                                                    } finally {
                                                        setLoading(false);
                                                        setHasClicked(true);
                                                    }
                                                }}
                                            >
                                                More Questions
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div id='big-white-box' className='h-100 d-flex' style={{ display: `${searchResults.length > 0 || searchingPlaceholderInfo?.userQuery ? 'block' : 'none'}` }}>
                                <div className='h-100 custom-scrollbar d-flex-grow'>
                                    {!(appStateContext?.state.selectedSearchResult?.userQuery) ? (
                                        <div className='bg-white-a-50 p-3 b-2F4250'>
                                            <div id='user-query' className='fs16-fw600 c-primary ps-1'>
                                                {cleanMessageForDisplay(searchingPlaceholderInfo?.userQuery || "")}
                                                <div className='spinner mt-2'></div>
                                            </div>
                                        </div>
                                    ) : null}
                                    {displaySearchResults()
                                    }
                                </div>
                            </div>

                            <div className='c-primary fs30-fw800 my-auto mx-auto t-a-center mt-5' style={{ display: `${searchResults.length > 0 || searchingPlaceholderInfo?.userQuery ? 'none' : 'block'}` }}>
                                {import.meta.env.VITE_APP_SHOW_FAQ === "ENABLED" && appStateContext?.state.faqList && appStateContext?.state.faqList.length > 0 && (
                                    <>
                                        {import.meta.env.VITE_APP_SHOW_ORG_LOGO === "ENABLED" && <img src={frontLogo} className='filter-normal-white mx-auto d-flex ' style={{ maxHeight: '80px' }} alt="" />}
                                        <div className='fs16-fw600 c-primary'>{import.meta.env.VITE_APP_SUBTITLE}</div>
                                        <div className='d-flex d-flex-column g-3 mt-3'>
                                            {appStateContext?.state.faqList.map((faq, index) => {
                                                return (
                                                    <button
                                                        key={index}
                                                        className='mx-auto bg-root ps-3 pe-3 p-1 c-default fs14-fw400 b-lightblue w-fit-content'
                                                        style={{ borderRadius: 4 }}
                                                        onClick={() => {
                                                            handleSearchInputClick(faq, currentSearchSessionId);
                                                        }}
                                                    >
                                                        {faq}
                                                    </button>
                                                )
                                            })}
                                        </div>
                                    </>
                                )
                                }
                                {
                                    !hasClicked && (
                                        <div className='d-flex d-flex-column g-3 mt-3'>
                                            {loading ? (
                                                <div className="spinner-login mx-auto" role="status">
                                                </div>
                                            ) : (
                                                <button
                                                    className='more-questions-btn mx-auto bg-blue ps-3 pe-3 p-1 br-16 c-default fs14-fw400 b-primary w-fit-content'
                                                    onClick={async () => {
                                                        try {
                                                            setLoading(true);
                                                            if (appStateContext?.state.faqList) {
                                                                const response = await generateMoreQuestions(appStateContext?.state.faqList);
                                                                if (response?.questions?.length) {
                                                                    appStateContext?.dispatch({ type: 'SET_FAQ_LIST', payload: [...appStateContext.state.faqList, ...response.questions] });
                                                                }
                                                            }
                                                        } catch (err) {
                                                            console.error("Failed to fetch more questions", err);
                                                        } finally {
                                                            setLoading(false);
                                                            setHasClicked(true);
                                                        }
                                                    }}
                                                >
                                                    More Questions
                                                </button>
                                            )}
                                        </div>
                                    )
                                }
                            </div>
                        </Layout.Content>
                    </>
                )

            }

            <div>
                {/* search history side bar */}
                {appStateContext?.state.screenSize && appStateContext?.state.screenSize < 3 ?
                    (
                        <div id='small-screen-overlay-menu' className='overlay bg-root' onClick={onClickDrawer}>
                            <div className='d-flex g-3 bg-none px-3' style={{ height: '10vh' }}>
                                <button className='d-flex h-fit-content my-auto' onClick={() => { setSmallScreenMenuActive(!smallScreenMenuActive) }}>
                                    <img
                                        className='filter-primary'
                                        src={barIcon}
                                        alt=""
                                        style={{ height: '30px', width: 'auto' }}
                                    />
                                </button>
                                {import.meta.env.VITE_APP_SHOW_ORG_LOGO === "ENABLED" && orgLogo && (
                                    <img alt="Org logo"
                                        src={orgLogo}
                                        className='my-auto filter-normal-white'
                                        aria-hidden="true"
                                        style={{ height: '40px', width: 'auto' }}
                                    />
                                )}
                                <img alt="NCS logo"
                                    src={NCSlogo}
                                    className='my-auto filter-normal-white'
                                    aria-hidden="true"
                                    style={{ height: '18px', width: 'auto' }}
                                />

                                <button onClick={toggleTheme} className='my-auto ms-auto'>
                                    <img src={halfCircleIcon} style={{ height: '25px', width: 'auto' }} alt="" />
                                </button>
                                <UserContextMenu />
                            </div>

                            {searchResults.length > 0 && <Button
                                className='bg-white-a-50 c-primary br-0 b-none w-100 position-relative pt-3 pb-3'
                                disabled={searchResults.length === 0}
                                onClick={handleNewChat}
                            >
                                New Session
                            </Button>}
                            {currentSearchStatus !== searchStatus.Processing ? (
                                <ChatHistoryPanel />
                            ) : (
                                <div className='bg-root' style={{ textAlign: "center" }}>
                                    <Spin className='bg-root c-defualt' tip={
                                        (
                                            <p>Search History disabled.<br></br>
                                                Please wait for search to be complete...</p>
                                        )
                                    }>
                                        <Alert
                                            style={{ height: "110px" }}
                                            type="warning"
                                        />
                                    </Spin>
                                </div>
                            )
                            }
                        </div>
                    ) : (
                        <Drawer title='Session History' className='bg-root c-default' id='session-history' onClose={toggleDrawer} open={isDrawerOpen} onClick={onClickDrawer}>
                            {currentSearchStatus !== searchStatus.Processing ? (
                                <>
                                    <div className='d-flex mb-2 g-3'>
                                        <RangePicker
                                            className='c-default bg-root b-primary br-16'
                                            onChange={(dates, dateStrings) => {
                                                // Set the selected date range to state or perform any action
                                                if (appStateContext?.state.chFilter) {
                                                    appStateContext.state.chFilter.date_filter = { start_date: dateStrings[0], end_date: dateStrings[1] };
                                                    appStateContext?.dispatch({ type: 'UPDATE_CH_FILTER', payload: appStateContext?.state.chFilter });
                                                }
                                            }}
                                            disabledDate={(current) => {
                                                const earliestDate = dayjs(earliest_date); // Replace with the actual earliest date
                                                const today = dayjs();
                                                return current && (current.isBefore(earliestDate, 'day') || current.isAfter(today, 'day'));
                                            }}
                                        />
                                    </div>
                                    {
                                        import.meta.env.VITE_APP_SHOW_CATEGORY_FILTER === "ENABLED" && (
                                            <Select
                                                mode="tags"
                                                className='w-100 catFilter mb-2'
                                                placeholder="Category"
                                                onChange={handleCatChangeFilter}
                                                options={CH_filter_category}
                                                menuItemSelectedIcon={<span className="custom-selected-icon" />}
                                            />
                                        )
                                    }

                                    <ChatHistoryPanel />
                                </>
                            ) : (
                                <div className='bg-root' style={{ textAlign: "center" }}>
                                    <Spin className='bg-root c-defualt' tip={
                                        (
                                            <p>Search History disabled.<br></br>
                                                Please wait for search to be complete...</p>
                                        )
                                    }>
                                        <Alert
                                            style={{ height: "110px" }}
                                            type="warning"
                                        />
                                    </Spin>
                                </div>
                            )
                            }
                        </Drawer>
                    )
                }
            </div>

            <Outlet />
        </Layout>
    );


};

export default NewLayout;
